/**
 * SectionSuggestionsPanel — Skerpa's grounded suggestions for a canvas
 * section. Hairline-separated rows in the editorial vocabulary used by the
 * canvas grid and property pane: open layout, mono labels, paired semantic
 * tokens, accent only on the primary action.
 */

import { useState, useEffect, useCallback } from 'react'
import { X, Sparkles, Loader2, AlertCircle, RefreshCw, Plus, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { canvasAIApi } from '@/lib/api/client'
import type { CanvasSection } from './canvasTemplates'

interface AISuggestion {
  id: string
  label: string
  description?: string
  entityType: string
  confidence: 'high' | 'medium' | 'low'
  reasoning?: string
}

interface SectionSuggestionsPanelProps {
  section: CanvasSection
  canvasInstanceUri: string
  accountId: string
  projectId: string
  apiKey: string
  canvasContext?: Record<string, string[]>
  onClose: () => void
  onAddSuggestion: (suggestion: {
    type: string
    label: string
    description?: string
    sectionId: string
    confidence: number
  }) => void
  className?: string
}

// ── Confidence chip → paired semantic tokens (already off-domain so they
// never clash with Market/Business/Solution colours)
const CONFIDENCE_CHIP: Record<AISuggestion['confidence'], string> = {
  high:   'text-[var(--color-success-fg)] bg-[var(--color-success-bg)]',
  medium: 'text-[var(--color-warning-fg)] bg-[var(--color-warning-bg)]',
  low:    'text-[var(--color-info-fg)]    bg-[var(--color-info-bg)]',
}

const CONFIDENCE_TO_NUMBER: Record<AISuggestion['confidence'], number> = {
  high: 0.9,
  medium: 0.7,
  low: 0.5,
}

export function SectionSuggestionsPanel({
  section,
  canvasInstanceUri,
  accountId,
  projectId,
  apiKey,
  canvasContext,
  onClose,
  onAddSuggestion,
  className,
}: SectionSuggestionsPanelProps) {
  const [suggestions, setSuggestions] = useState<AISuggestion[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set())

  const fetchSuggestions = useCallback(async () => {
    if (!apiKey) return
    setIsLoading(true)
    setError(null)
    try {
      const result = await canvasAIApi.generateSuggestions(
        accountId, projectId, canvasInstanceUri,
        { sectionKey: section.id, apiKey, canvasContext, limit: 8 },
      )
      setSuggestions(result.suggestions)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch suggestions:', err)
      setError(err instanceof Error ? err.message : 'Failed to generate suggestions')
    } finally {
      setIsLoading(false)
    }
  }, [apiKey, accountId, projectId, canvasInstanceUri, section.id, canvasContext])

  useEffect(() => {
    fetchSuggestions()
    setAddedIds(new Set())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section.id])

  const handleAdd = (suggestion: AISuggestion) => {
    onAddSuggestion({
      type: suggestion.entityType,
      label: suggestion.label,
      description: suggestion.description,
      sectionId: section.id,
      confidence: CONFIDENCE_TO_NUMBER[suggestion.confidence] ?? 0.7,
    })
    setAddedIds((prev) => new Set([...prev, suggestion.id]))
  }

  const handleAddAllHighConfidence = () => {
    suggestions
      .filter((s) => s.confidence === 'high' && !addedIds.has(s.id))
      .forEach(handleAdd)
  }

  const highConfidenceCount = suggestions.filter(
    (s) => s.confidence === 'high' && !addedIds.has(s.id)
  ).length
  const sourceCount = Object.keys(canvasContext || {}).length

  return (
    <div className={cn('flex flex-col bg-bg h-full', className)}>
      {/* ── Header ── */}
      <header className="flex-shrink-0 px-5 pt-4 pb-3 border-b border-border-subtle">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.1em] text-text-muted mb-1">
              <Sparkles className="h-3 w-3 text-accent" />
              <span>Skerpa</span>
              <span className="text-fg-faint">·</span>
              <span className="truncate">{section.title}</span>
            </div>
            <h2 className="text-[15px] font-semibold text-text leading-tight tracking-[-0.005em]">
              Suggestions
            </h2>
          </div>
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <button
              type="button"
              onClick={fetchSuggestions}
              disabled={isLoading}
              className="h-7 w-7 grid place-items-center rounded-md text-text-muted hover:text-text hover:bg-warm-100 transition-colors disabled:opacity-40"
              title="Regenerate"
              aria-label="Regenerate suggestions"
            >
              <RefreshCw className={cn('h-3.5 w-3.5', isLoading && 'animate-spin')} />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="h-7 w-7 grid place-items-center rounded-md text-text-muted hover:text-text hover:bg-warm-100 transition-colors"
              title="Close"
              aria-label="Close suggestions"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Approve all high — surfaces only when there's something to apply
            in one shot. Quiet text button, not a primary CTA. */}
        {!isLoading && highConfidenceCount >= 2 && (
          <button
            type="button"
            onClick={handleAddAllHighConfidence}
            className="mt-2 inline-flex items-center gap-1 text-[12px] text-accent hover:underline underline-offset-2"
          >
            Apply all {highConfidenceCount} high-confidence
          </button>
        )}
      </header>

      {/* ── Content ── */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {isLoading ? (
          <div className="px-5 py-8 flex items-center gap-2 text-text-muted">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-accent" />
            <span className="text-[12.5px]">Reading your canvas, drafting suggestions…</span>
          </div>
        ) : error ? (
          <div className="px-5 py-6">
            <div className="flex items-start gap-2.5">
              <AlertCircle className="h-3.5 w-3.5 text-danger-fg flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-[13px] text-text font-medium">Couldn't generate</p>
                <p className="text-[12px] text-text-muted leading-relaxed mt-0.5">{error}</p>
                <button
                  type="button"
                  onClick={fetchSuggestions}
                  className="mt-2 text-[12px] text-accent hover:underline underline-offset-2"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        ) : suggestions.length === 0 ? (
          <div className="px-5 py-8">
            <p className="text-[13px] text-text font-medium mb-1">Nothing to suggest yet.</p>
            <p className="text-[12.5px] text-text-muted leading-relaxed">
              Add content to other sections so Skerpa has something to ground its suggestions in,
              then refresh.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border-subtle">
            {suggestions.map((suggestion) => {
              const isAdded = addedIds.has(suggestion.id)
              return (
                <li
                  key={suggestion.id}
                  className={cn(
                    'group relative flex items-start gap-3 px-5 py-3.5 transition-colors',
                    isAdded ? 'opacity-60' : 'hover:bg-warm-100',
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <h3 className={cn(
                        'text-[13.5px] font-semibold text-text leading-snug',
                        isAdded && 'line-through text-text-muted',
                      )}>
                        {suggestion.label}
                      </h3>
                      <span className={cn(
                        'inline-block font-mono text-[9.5px] uppercase tracking-[0.06em] px-1.5 py-px rounded-[3px] flex-shrink-0',
                        CONFIDENCE_CHIP[suggestion.confidence],
                      )}>
                        {suggestion.confidence}
                      </span>
                    </div>
                    {suggestion.description && (
                      <p className="text-[12.5px] text-text-secondary leading-relaxed mt-1 max-w-[58ch]">
                        {suggestion.description}
                      </p>
                    )}
                    {suggestion.reasoning && (
                      <p className="text-[11.5px] text-text-muted leading-relaxed mt-1 italic max-w-[58ch]">
                        {suggestion.reasoning}
                      </p>
                    )}
                  </div>

                  {/* Apply / Applied — quiet at rest, accent on hover. */}
                  {isAdded ? (
                    <span
                      className="h-7 w-7 grid place-items-center rounded-full text-[var(--color-success-fg)] flex-shrink-0 mt-0.5"
                      title="Added to canvas"
                      aria-label={`${suggestion.label} added`}
                    >
                      <Check className="h-4 w-4" />
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleAdd(suggestion)}
                      className={cn(
                        'h-7 w-7 grid place-items-center rounded-full flex-shrink-0 mt-0.5',
                        'border border-border bg-bg-card text-text-muted',
                        'hover:bg-accent hover:text-white hover:border-accent',
                        'opacity-0 group-hover:opacity-100 focus:opacity-100',
                        'transition-[opacity,background-color,color,border-color] duration-150',
                      )}
                      title={`Add ${suggestion.label}`}
                      aria-label={`Add ${suggestion.label}`}
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {/* ── Footer · grounding indicator ── */}
      <footer className="flex-shrink-0 px-5 py-2.5 border-t border-border-subtle bg-warm-50/60">
        <p className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-fg-faint">
          {sourceCount === 0
            ? 'Grounded in this section'
            : `Grounded in ${sourceCount} ${sourceCount === 1 ? 'section' : 'sections'}`}
        </p>
      </footer>
    </div>
  )
}
